// M004-F0744 sections 3.1, 5 and 10: the main pane - header (name, state chip, running totals, "N to check"), the filter/search row, the
// grid/list of receipts, the drop zone, and the pane states (creating, skeleton tiles, error with code and Try again, no such
// envelope, empty envelope). Totals sum CONFIRMED amounts only. A filter never changes a total.
//
// M004-F0745 (move receipts, design section 6): the tiles are ONE role="listbox" (aria-multiselectable) of role="option" buttons with a
// roving tabindex; the keyboard owns arrows / Home / End, Space (select), Shift+Arrow (extend), Ctrl+A (all), M (Move to…), Enter (open),
// Esc (clear). A mouse Ctrl/Shift+click selects; once anything is selected a plain click toggles (select mode) instead of opening. On
// touch a 350 ms long-press selects and a 44 px checkbox toggles. Tiles of an envelope that cannot give receipts are not draggable.
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import EnvelopeUploadZone from './EnvelopeUploadZone';
import OcrChip from './OcrChip';
import ReceiptThumb from './ReceiptThumb';
import { statusChip } from './envelopeModel';
import {
  FILTERS, UNSORTED_LABEL, confirmedAmountText, formatMoney, formatShortDate, isUnsorted, receiptChip, receiptDate, receiptHeadline,
  resultCountText, stateChipFor,
} from './envelopeWorkspaceModel';

const GridIcon = () => <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false"><path fill="currentColor" d="M1 1h6v6H1V1Zm8 0h6v6H9V1ZM1 9h6v6H1V9Zm8 0h6v6H9V9Z" /></svg>;
const ListIcon = () => <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false"><path fill="currentColor" d="M1 2h14v2.5H1V2Zm0 4.75h14v2.5H1v-2.5ZM1 11.5h14V14H1v-2.5Z" /></svg>;

export function Totals({ totals }) {
  if (!totals) return null;
  const multi = totals.currencies.length > 1;
  return (
    <p className="ew-totals" data-testid="envelope-totals">
      <span>{totals.count} {totals.count === 1 ? 'receipt' : 'receipts'}</span>
      {totals.currencies.map((row) => (
        <span key={row.currency} className="ew-total-money" data-testid={`envelope-total-${row.currency}`}>
          <strong>{formatMoney(row.total_minor, row.currency)}</strong>
          {row.vat_minor > 0 ? <span className="ew-muted">{` · VAT ${multi ? `${row.currency} ` : ''}${formatMoney(row.vat_minor)}`}</span> : null}
        </span>
      ))}
    </p>
  );
}

const LONG_PRESS_MS = 350;
const LONG_PRESS_SLOP = 10;

const CheckIcon = () => <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false"><path fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" d="m3.5 8.5 3 3 6-7" /></svg>;

function Tile({ receipt, orgId, envelopeId, move, duplicate = false }) {
  const id = receipt.receipt_document_id;
  const chip = receiptChip(receipt);
  const headline = receiptHeadline(receipt);
  const amount = confirmedAmountText(receipt);
  const date = formatShortDate(receiptDate(receipt));
  const press = useRef(null);
  const suppressClick = useRef(false);
  const selected = move.selection.has(id);
  const moving = Boolean(move.moving?.has(id)); // #264: the Ledger has not finally answered this receipt's move
  const draggable = move.canGive && !move.offline && !moving;

  const clearPress = () => { if (press.current) { clearTimeout(press.current.timer); press.current = null; } };
  useEffect(() => clearPress, []);

  const onPointerDown = (event) => {
    if (event.pointerType !== 'touch' || !move.canGive) return;
    const start = { x: event.clientX, y: event.clientY };
    clearPress();
    press.current = { start, timer: setTimeout(() => { press.current = null; suppressClick.current = true; move.onLongPress(id); }, LONG_PRESS_MS) };
  };
  const onPointerMove = (event) => {
    if (press.current && Math.hypot(event.clientX - press.current.start.x, event.clientY - press.current.start.y) > LONG_PRESS_SLOP) clearPress();
  };
  const onPointerEnd = () => { clearPress(); if (suppressClick.current) setTimeout(() => { suppressClick.current = false; }, 400); };

  return (
    <li
      className={`ew-tile${selected ? ' ew-tile-selected' : ''}${move.dragging.has(id) ? ' ew-tile-dragging' : ''}`}
      data-testid={`envelope-receipt-tile-${id}`}
      data-flip-id={id}
      data-status={chip.tone}
      data-restored={move.restored.has(id) ? 'true' : undefined}
      data-moving={moving ? 'true' : undefined}
      aria-busy={moving ? 'true' : undefined}
      role="none"
      draggable={draggable}
      onDragStart={(event) => move.onDragStart(event, receipt)}
      onDragEnd={move.onDragEnd}
    >
      <button
        type="button"
        role="option"
        aria-selected={selected}
        className="ew-tile-open"
        disabled={moving}
        tabIndex={move.activeId === id ? 0 : -1}
        data-receipt-id={id}
        data-testid={`envelope-receipt-open-${id}`}
        aria-haspopup="dialog"
        onFocus={() => move.onActive(id)}
        onContextMenu={(event) => { if (event.nativeEvent?.pointerType === 'touch' || press.current || suppressClick.current) event.preventDefault(); }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        onClick={(event) => {
          if (suppressClick.current) { event.preventDefault(); return; }
          move.onClickTile(event, receipt, event.currentTarget);
        }}
      >
        <ReceiptThumb orgId={orgId} envelopeId={envelopeId} receipt={receipt} />
        {duplicate ? <span className="ew-tile-dup" data-testid={`envelope-receipt-duplicate-${id}`}>Duplicate</span> : null}
        {move.canGive ? <span className="ew-tile-check" data-testid={`envelope-receipt-check-${id}`} aria-hidden="true"><span className="ew-tile-check-box"><CheckIcon /></span></span> : null}
        <span className="ew-tile-text">
          <span className={`ew-tile-title${headline.suggested ? ' ew-suggested' : ''}`}>{headline.text}</span>
          <span className="ew-tile-date">{date || '—'}</span>
          <span className="ew-tile-amount">{amount || '—'}</span>
          <OcrChip receipt={receipt} testId={`envelope-receipt-chip-${id}`} />
        </span>
      </button>
    </li>
  );
}

// Tiles that stay glide to their new place when others leave or return (Web Animations, skipped under prefers-reduced-motion).
function useFlip(listRef, key) {
  const rects = useRef(new Map());
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const next = new Map();
    list.querySelectorAll('[data-flip-id]').forEach((node) => {
      const box = node.getBoundingClientRect();
      next.set(node.getAttribute('data-flip-id'), box);
      const before = rects.current.get(node.getAttribute('data-flip-id'));
      if (!reduce && before && typeof node.animate === 'function') {
        const dx = before.left - box.left;
        const dy = before.top - box.top;
        if (Math.abs(dx) > 1 || Math.abs(dy) > 1) node.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], { duration: 220, easing: 'cubic-bezier(.2,.8,.2,1)' });
      }
    });
    rects.current = next;
  }, [listRef, key]);
}

function SelectionBar({ move }) {
  if (!move.selection.size) return null;
  const n = move.selection.size;
  return (
    <div className="ew-selbar" data-testid="envelope-selection-bar">
      <span className="ew-selbar-count" role="status" data-testid="envelope-selection-count">{`${n} selected`}</span>
      <div className="ew-selbar-menu">
        <button
          type="button"
          className="ew-btn ew-btn-primary ew-selbar-move"
          data-testid="envelope-move-to"
          aria-haspopup="menu"
          aria-expanded={Boolean(move.menuOpen)}
          disabled={move.offline}
          onClick={() => move.onOpenMenu('button')}
        >
          Move to…
        </button>
        {move.menuSlot}
      </div>
      <button type="button" className="ew-btn" data-testid="envelope-selection-clear" onClick={move.onClear}>Clear selection</button>
    </div>
  );
}

export default function EnvelopePane({
  orgId, envelope, details, totals, filter, onFilter, view, onView, onRetry, onDetails,
  readOnlyReason, lockedReason, onStored, onBusy = null, visible, move, claimUi = null, duplicateIds = null, knownSha256s = [], placeOf = null,
}) {
  const listRef = useRef(null);
  const visibleKey = visible.map((item) => item.receipt_document_id).join(',');
  useFlip(listRef, visibleKey);
  useLayoutEffect(() => {
    if (!move.focusRequest.tick) return;
    const root = listRef.current;
    const target = move.focusRequest.id && root ? root.querySelector(`[data-receipt-id="${move.focusRequest.id}"]`) : null;
    if (target) target.focus();
    else {
      const heading = document.getElementById('ew-pane-title');
      if (heading) heading.focus();
    }
  }, [move.focusRequest]);

  const onListKeyDown = (event) => {
    const node = event.target.closest?.('[role="option"]');
    if (!node || event.altKey) return;
    const id = node.getAttribute('data-receipt-id');
    const ids = visible.map((item) => item.receipt_document_id);
    const at = ids.indexOf(id);
    const options = Array.from(listRef.current.querySelectorAll('[role="option"]'));
    const firstTop = options.length ? options[0].parentElement.offsetTop : 0;
    const columns = Math.max(1, options.filter((el) => el.parentElement.offsetTop === firstTop).length);
    const jump = (to) => {
      const clamped = Math.max(0, Math.min(ids.length - 1, to));
      event.preventDefault();
      move.onFocusTile(ids[clamped], event.shiftKey);
    };
    switch (event.key) {
      case 'ArrowRight': jump(at + 1); break;
      case 'ArrowLeft': jump(at - 1); break;
      case 'ArrowDown': jump(at + columns); break;
      case 'ArrowUp': jump(at - columns); break;
      case 'Home': jump(0); break;
      case 'End': jump(ids.length - 1); break;
      case ' ': event.preventDefault(); move.onToggle(id); break;
      case 'Enter': event.preventDefault(); move.onOpen(visible[at], node); break;
      case 'a': case 'A':
        if (event.ctrlKey || event.metaKey) { event.preventDefault(); move.onSelectAll(); }
        break;
      case 'm': case 'M':
        if (!event.ctrlKey && !event.metaKey) { event.preventDefault(); move.onOpenMenu('tile', id); }
        break;
      case 'Escape':
        if (move.selection.size) { event.preventDefault(); event.stopPropagation(); move.onClear(); }
        break;
      default: break;
    }
  };

  const unsorted = isUnsorted(envelope);
  const title = unsorted ? UNSORTED_LABEL : envelope.name;
  const chip = stateChipFor(envelope) || (envelope.status === 'deleted' ? statusChip(envelope) : null);
  const status = details?.status;
  const receipts = details?.receipts || [];
  const uploadNotice = envelope.pending ? 'Creating the envelope…' : readOnlyReason || lockedReason || '';
  const showControls = status === 'ready' && receipts.length > 0;
  const isEmpty = status === 'ready' && receipts.length === 0;

  return (
    <section className="ew-pane" aria-labelledby="ew-pane-title" data-testid="envelopes-pane" data-state={envelope.pending ? 'creating' : status}>
      <header className="ew-pane-head">
        <div className="ew-pane-titles">
          <h2 id="ew-pane-title" tabIndex={-1} data-testid="envelope-pane-title">{title}</h2>
          {envelope.locked ? <span className="ew-muted" data-testid="envelope-pane-locked">{lockedReason}</span> : null}
          {chip ? <span className={`ew-chip ew-chip-${chip.tone}`} data-testid="envelope-pane-chip">{chip.label}</span> : null}
        </div>
        <div className="ew-pane-actions">
          {claimUi?.button ? (
            <span className="ew-claim-action">
              <button
                type="button"
                className="ew-btn ew-btn-primary"
                ref={claimUi.button.ref}
                data-testid="envelope-claim-open"
                disabled={Boolean(claimUi.button.reason)}
                aria-describedby={claimUi.button.reason ? 'ew-claim-why' : undefined}
                aria-expanded={claimUi.button.expanded}
                onClick={claimUi.button.onClick}
              >{claimUi.button.label}</button>
              {claimUi.button.reason ? <span id="ew-claim-why" className="ew-muted ew-hint" data-testid="envelope-claim-why">{claimUi.button.reason}</span> : null}
            </span>
          ) : null}
        {!envelope.pending ? <button type="button" className="ew-btn" data-testid="envelope-details-open" aria-haspopup="dialog" onClick={onDetails}>Details</button> : null}
        </div>
      </header>

      {claimUi?.banner}
      {claimUi?.panel}

      {envelope.pending ? <p className="ew-muted ew-creating-note" data-testid="envelopes-pane-creating">Creating… you can drop receipts here in a moment.</p> : null}
      {!envelope.pending && totals ? (
        <div className="ew-summary">
          <Totals totals={totals} />
          {totals.toCheck > 0 ? (
            <button type="button" className="ew-link" data-testid="envelope-to-check" onClick={() => onFilter({ status: 'needs_checking' })}>{totals.toCheck} to check</button>
          ) : null}
        </div>
      ) : null}

      {showControls ? <SelectionBar move={move} /> : null}

      {showControls ? (
        <div className="ew-toolbar" role="search">
          <label className="ew-visually-hidden" htmlFor="ew-search">Search receipts by merchant, note, date or amount</label>
          <input
            id="ew-search"
            type="search"
            className="ew-search"
            value={filter.query}
            placeholder="Search merchant, date, amount (>50)"
            data-testid="envelopes-search"
            onChange={(event) => onFilter({ query: event.target.value })}
          />
          <label className="ew-visually-hidden" htmlFor="ew-status">Show</label>
          <select id="ew-status" className="ew-select" value={filter.status} data-testid="envelopes-status-filter" onChange={(event) => onFilter({ status: event.target.value })}>
            {FILTERS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          <div className="ew-view-toggle" role="group" aria-label="Layout">
            <button type="button" aria-pressed={view === 'grid'} aria-label="Grid" data-testid="envelopes-view-grid" onClick={() => onView('grid')}><GridIcon /></button>
            <button type="button" aria-pressed={view === 'list'} aria-label="List" data-testid="envelopes-view-list" onClick={() => onView('list')}><ListIcon /></button>
          </div>
          <p className="ew-muted ew-result-count" aria-live="polite" data-testid="envelopes-result-count">{resultCountText(visible.length, receipts.length)}</p>
        </div>
      ) : null}

      {status === 'loading' ? (
        <ul className="ew-grid" data-view={view} data-testid="envelopes-pane-skeleton" aria-hidden="true">
          {[0, 1, 2, 3].map((n) => <li key={n} className="ew-tile ew-tile-skel"><span className="ew-skel ew-thumb-skel" /><span className="ew-skel ew-skel-line" /><span className="ew-skel ew-skel-line ew-skel-short" /></li>)}
        </ul>
      ) : null}

      {status === 'error' ? (
        <div className="ew-error" role="alert" data-testid="envelope-error">
          <p>{details.error.message}</p>
          {details.error.code ? <p>Error code: <code data-testid="envelope-error-code">{details.error.code}</code></p> : null}
          <button type="button" className="ew-btn" data-testid="envelope-retry" onClick={onRetry}>Try again</button>
        </div>
      ) : null}
      {status === 'notfound' ? <p className="ew-error" role="alert" data-testid="envelope-not-found">No such envelope</p> : null}

      {showControls && visible.length > 0 ? (
        <ul
          ref={listRef}
          className="ew-grid"
          data-view={view}
          data-testid="envelope-receipts"
          data-selecting={move.selection.size ? 'true' : undefined}
          role="listbox"
          aria-multiselectable="true"
          aria-labelledby="ew-pane-title"
          onKeyDown={onListKeyDown}
        >
          {visible.map((receipt) => (
            <Tile key={receipt.receipt_document_id} receipt={receipt} orgId={orgId} envelopeId={envelope.envelope_id} move={move} duplicate={Boolean(duplicateIds && duplicateIds.has(receipt.receipt_document_id))} />
          ))}
        </ul>
      ) : null}
      {filter.ids ? (
        <p className="ew-notice" role="status" data-testid="envelope-ids-filter">
          Showing only the {visible.length} {visible.length === 1 ? 'receipt' : 'receipts'} the claim review named.
          {' '}
          <button type="button" className="ew-link" data-testid="envelope-ids-filter-clear" onClick={() => onFilter({ ids: null })}>Show all</button>
        </p>
      ) : null}
      {showControls && visible.length === 0 ? (
        <p className="ew-empty-filter" data-testid="envelopes-no-match">
          No receipts match this filter.
          {' '}
          <button type="button" className="ew-link" onClick={() => onFilter({ query: '', status: 'all', ids: null })}>Clear filter</button>
        </p>
      ) : null}

      {/* ONE upload zone for both layouts (enlarged while the envelope is empty): a second instance would unmount on the first stored
          receipt and take the queue's rows and the "N of M stored" line with it (#185). */}
      {isEmpty || showControls ? (
        <div className={isEmpty ? 'ew-empty-pane' : undefined} data-testid={isEmpty ? 'envelopes-pane-empty' : undefined}>
          <EnvelopeUploadZone orgId={orgId} envelopeId={envelope.envelope_id} status={envelope.status} disabledNotice={uploadNotice} large={isEmpty} onStored={onStored} onBusyChange={onBusy} knownSha256s={knownSha256s} placeOf={placeOf} />
        </div>
      ) : null}
    </section>
  );
}
